/**
 * Every string `/settings` renders.
 *
 * **No configuration value appears anywhere in this file.** Every row on the
 * page *is* a configuration value, and none of them is chosen here — the values,
 * the descriptions, the secret classification, and the editability all arrive
 * from the service that owns the key. What this file carries is the console's
 * own wording: the section headings, the column names, and the sentences that
 * state the surface's limits.
 *
 * **Design rationale is barred from the page.** Why a one-key section was left
 * at one key belongs in the companion spec, where reviewers read it; on the
 * screen it takes space from the sentence that tells an operator what the
 * service's settings actually govern.
 */
export const copy = {
  crumbs: {
    console: 'لوحة التشغيل',
  },

  crumb: 'الإعدادات',

  head: {
    title: 'الإعدادات',
    lede: 'تهيئة التشغيل التي تنشرها كلّ خدمة في جدولها. اختر خدمة لتفتح مفاتيحها وحدها. المفاتيح مبذورة من الشفرة: لا تُنشأ هنا ولا تُحذف ولا يُعاد تسميتها، وما يُحرَّر هو القيمة والوصف وحدهما. وكلّ كتابة تُؤكَّد باسم المفتاح قبل أن تُنفَّذ.',
  },

  /** The index: one entry per service, and nothing is read until one is opened. */
  index: {
    heading: 'الخدمات',
    /** Named on the card, so «افتح» is not the only thing that says what happens. */
    open: 'افتح مفاتيح هذه الخدمة',
    /** Stated once on the index rather than measured, because the index reads nothing. */
    unread: 'تُقرأ مفاتيح الخدمة عند فتحها.',
    empty: 'لا خدمة يديرها حسابك. تهيئة التشغيل من صلاحيّة المدير في كلّ خدمة على حدة.',
  },

  /** The strip that carries every other service from inside one of them. */
  strip: {
    label: 'الخدمات',
  },

  /** One service's own page. */
  service: {
    /** `٤٢ مفتاحاً في هذا الجدول.` — counted from the rows that drew, never written. */
    count: (keys: string) => `${keys} مفتاحاً في هذا الجدول.`,
    back: 'كلّ الخدمات',
    /** The segment named no service this console publishes a section for. */
    unknownTitle: 'لا خدمة بهذا الاسم',
    unknownBody: 'العنوان الذي فتحته لا يسمّي خدمةً تنشر جدول تهيئة. عد إلى قائمة الخدمات واختر واحدة منها.',
  },

  /** The surface's own boundary, in plain language. A statement, not an error. */
  contract: {
    title: 'ما يفعله هذا السطح وما لا يفعله',
    body: 'يقرأ ويحرّر. لا يضيف مفتاحاً ولا يحذف واحداً ولا يغيّر اسمه — تلك ثلاثتها تقع في شفرة الخدمة نفسها. والمفتاح الذي تصنّفه خدمته سرّاً لا تُعرض قيمته هنا أصلاً: يُقرأ مقنّعاً، ويُدوَّر بدل أن يُكشف.',
    /** Counted from the sections this account administers, never written here. */
    counts: (services: string) => `${services} خدمات يديرها حسابك تنشر جداول تهيئة.`,
  },

  columns: {
    key: 'المفتاح',
    value: 'القيمة',
    description: 'الوصف',
    actions: 'أفعال',
  },

  badges: {
    readonly: 'للقراءة فقط',
    secret: 'سرّ',
  },

  /** The mask is a constant. It is not a truncation of anything, and no payload feeds it. */
  mask: '••••••••••••',
  maskLabel: 'قيمة مقنّعة',
  rotatedAt: 'آخر تدوير:',
  neverRotated: 'لم يُدوَّر بعد',

  rowMenu: {
    label: (key: string) => `أفعال المفتاح ${key}`,
    edit: 'حرّر القيمة والوصف',
    /** A secret's value is not edited from here at all, so the entry names only what is. */
    editSecret: 'حرّر الوصف',
    rotate: 'دوّر المفتاح',
  },

  sections: {
    auth: {
      heading: 'المصادقة والحسابات',
      context:
        'هويّة رقم الهاتف، والرمز لمرّة واحدة، وكلمة المرور الاختيارية، وسطح الجدار، ومفاتيح الخدمة إلى الخدمة.',
      caption: 'مفاتيح تهيئة خدمة المصادقة والحسابات',
    },
    content: {
      heading: 'المحتوى والفهرس',
      context:
        'سياسة النوع، وآلة حالات النشر، وحقول ما قبل النشر، ومزوّد البيانات الخارجيّ، ومفردة أدوار النسبة، ومفردتا شكل الرفّ ومصدره، ودورة حياة المقال، وما يفعله الحذف في الفهرس.',
      caption: 'مفاتيح تهيئة خدمة المحتوى والفهرس',
    },
    assets: {
      heading: 'الأصول والنسخ',
      context: 'العرض المجّانيّ، وأغراض الصور ونسبها، وسلّم الدقّات، ونمط التسليم، وصيغ الرفع وحدودها.',
      caption: 'مفاتيح تهيئة خدمة الأصول والنسخ',
    },
    search: {
      heading: 'البحث والاكتشاف',
      context: 'ما يضبط لوحة المرشّحات ومفرداتها، ومصادر الرفوف المشتقّة من الحداثة والرواج.',
      caption: 'مفاتيح تهيئة خدمة البحث والاكتشاف',
    },
    engagement: {
      heading: 'التفاعل',
      context: 'إشراف التعليقات قبل الظهور، وأنواع التفاعل، وحدود الردود والعدّادات.',
      caption: 'مفاتيح تهيئة خدمة التفاعل',
    },
    analytics: {
      heading: 'التقارير والتقدّم',
      context: 'تسجيل موضع المشاهدة، وعتبة الإكمال، وما تحسبه لوحات التقارير.',
      caption: 'مفاتيح تهيئة خدمة التقارير والتقدّم',
    },
    notification: {
      heading: 'الإشعارات',
      context: 'كتالوج الأحداث، وقنوات التسليم، وتفضيل المستخدم لكلّ نوع.',
      caption: 'مفاتيح تهيئة خدمة الإشعارات',
    },
    finance: {
      heading: 'الاستحقاق والاشتراك',
      context: 'مصادر الاستحقاق ونطاقه، ومسار السداد، والعملة، ودورة التفعيل والانتهاء.',
      caption: 'مفاتيح تهيئة خدمة الاستحقاق والاشتراك',
    },
    support: {
      heading: 'الحالات',
      context: 'مفردات الحالات، وأسباب كلّ نوع من أنواعها، وحدود الطابور.',
      caption: 'مفاتيح تهيئة خدمة الحالات',
    },
  },

  /** Why three repos have no section. Named rather than left to be noticed. */
  absence:
    'لا يظهر هنا قسمٌ للمنطق المشترك ولا لسجلّ النشاط: المنطق المشترك مكتبة تُستورَد لا خدمةٌ تُنشَر، فلا قاعدة بيانات لها ولا جدول تهيئة؛ وسجلّ النشاط غير مفعَّل في هذا الإصدار، فلا قاعدة له ولا جدول. والواجهات الثلاث لا تنشر جداول تهيئة أصلاً — التهيئة عقدُ خدمةٍ خلفية.',

  dialog: {
    title: 'حرّر الإعداد',
    keyLabel: 'المفتاح',
    keyHint: 'مبذور من شفرة الخدمة. لا يُنشأ من هنا ولا يُحذف ولا يُعاد تسميته.',
    valueLabel: 'القيمة',
    valueHint: 'قيمة تقرؤها الخدمة لا يقرؤها إنسان: أرقامها لاتينية ولا مسافة حول الفاصلة.',
    descriptionLabel: 'الوصف',
    descriptionHint: 'ما يقرؤه المشغّل التالي ليعرف ماذا يكسر تغييرُ هذه القيمة.',
    /** The edit dialog does not write. It hands over to the confirmation, which does. */
    commit: 'راجع التغيير',
    cancel: 'ألغِ',
  },

  confirm: {
    heading: (key: string) => `اكتب القيمة الجديدة لـ${key}؟`,
    headingDescriptionOnly: (key: string) => `احفظ وصف ${key}؟`,
    body: 'تسري القيمة على الخدمة فور الكتابة، ولا سجلّ يُراجَع بعدها: خطوة التأكيد هذه هي الحارس الوحيد الذي يشحن في هذا الإصدار.',
    from: 'من',
    to: 'إلى',
    commit: 'اكتب القيمة',
  },

  rotate: {
    heading: (key: string) => `دوّر ${key}؟`,
    body: 'تسري القيمة الجديدة فوراً، وتتوقّف كلّ مناداة ما تزال تحمل القديمة حتى تُحدَّث. القيمة القائمة لا تُعرض هنا ولا في أي مكان آخر.',
    valueLabel: 'القيمة الجديدة',
    valueHint: 'احفظ نسخة منها في مكانها الآمن قبل أن تغلق النافذة: لا تُعرض بعدها ولا تُسترجَع من هنا.',
    required: 'ألصق القيمة الجديدة.',
    commit: 'دوّر المفتاح',
  },

  states: {
    loading: 'جارٍ تحميل التهيئة',
    empty: 'لا مفتاح في هذا الجدول.',
  },

  toast: {
    written: (key: string) => `كُتبت قيمة ${key}.`,
    described: (key: string) => `حُفظ وصف ${key}.`,
    rotated: (key: string) => `دُوّر ${key}. حدّث كلّ ما يستعمله.`,
    /** A no-op never reaches the confirmation, and never reaches the wire either. */
    unchanged: 'لا تغيير — لم يُكتب شيء.',
  },
} as const;
